import { CatalogCollection, CatalogLesson } from "../../client/svcContent";
import { ContentCardProps, ContentType } from "../components/ContentCard/contentCardTypes";
import { Level } from "../contexts/Level/LevelContextProvider";
import { levelToVariant } from "./colorUtils";

const LEVEL_NAME_REPLACE = "%{default_level_name}";
const LEVEL_DESC_REPLACE = "%{default_level_description}";

export const LESSON_TYPE_MAP: Record<string, ContentType> = {
  VideoLesson: "video",
  VideoWithCodingExercise: "video",
  CodingChallenge: "codingChallenge",
  CtfChallenge: "ctfChallenge",
  CodingExerciseItem: "exercise",
  HandsOnLesson: "breakfix",
  HackingChallenge: "hackingChallenge",
  AppLab: "app_lab",
  LESSON_CONFIGURATION_TYPE_UNSPECIFIED: "video",
};

export function replaceSubstringIfExists(
  originalString: string,
  searchSubstring: string,
  replacement: string
): string {
  if (originalString.includes(searchSubstring)) {
    return originalString.replace(searchSubstring, replacement);
  }
  return originalString;
}

/**
 * The card mappers below consume the catalog API types returned by
 * `ContentClient.getCollections()`/`getLessons()`, which Catalog passes through
 * verbatim. Every proto3 field is optional in the generated types, hence the
 * fallbacks below.
 */
type RawLesson = CatalogLesson & {
  // Not part of the catalog API Lesson; kept so a payload that does include an id
  // still maps it (the card id falls back to "" otherwise).
  lessonId?: string;
};

export const mapCollectionToCard = (
  collection: CatalogCollection,
  levelsDetails?: Level[]
): ContentCardProps => {
  const associatedLevel = levelsDetails?.find(level => level.id === collection.levelId);
  const name = replaceSubstringIfExists(
    collection.name ?? "",
    LEVEL_NAME_REPLACE,
    associatedLevel?.name || "Level"
  );

  return {
    // The API declares collectionType as a plain string; runtime values are "path"/"quest".
    contentType: collection.collectionType as ContentType,
    id: collection.collectionKey || "",
    lessonType: "path",
    name: name,
    title: name,
    description: replaceSubstringIfExists(
      collection.description || "",
      LEVEL_DESC_REPLACE,
      associatedLevel?.description || "Level description"
    ),
    difficulty: associatedLevel ? associatedLevel.name : undefined,
    cardSize: "small",
    variant: levelToVariant(collection.levelId ?? 0),
    estimatedTimeToComplete: collection.combinedDuration,
    lessonsInEnrollment: collection.lessonCount || undefined,
    handsOnLessonCount: collection.handsOnLessonCount || undefined,
    videoLessonCount: collection.videoLessonCount || undefined,
  };
};

export const mapCollectionsToCards = (
  collections: CatalogCollection[],
  levelsDetails?: Level[]
): ContentCardProps[] => {
  return collections.map(collection => mapCollectionToCard(collection, levelsDetails));
};

export const mapLessonToCard = (lesson: RawLesson): ContentCardProps => {
  const contentType = LESSON_TYPE_MAP[lesson.type ?? ""] || "video";

  return {
    contentType,
    lessonType: contentType,
    id: lesson.lessonId || "",
    lessonKey: lesson.lessonKey || "",
    title: lesson.name || "",
    description: lesson.description || "",
    estimatedTime: lesson.duration || undefined,
  };
};

export const mapLessonsToCards = (lessons: RawLesson[]): ContentCardProps[] => {
  return lessons.map(mapLessonToCard);
};
